import { DateTime } from 'luxon';

export const getCurrentDate = (format: string = 'yyyy-MM-dd'): string => {
  return DateTime.local().toFormat(format);
};

export const getCurrentTime = (): string => {
  return DateTime.local().toISO();
};

export const getTomorrowsDate = (format: string = 'yyyy-MM-dd'): string => {
  return DateTime.local().plus({ days: 1 }).toFormat(format);
};

export const getYesterdaysDate = (format: string = 'yyyy-MM-dd'): string => {
  return DateTime.local().minus({ days: 1 }).toFormat(format);
};

export const getThisWeekDateRange = (format: string = 'yyyy-MM-dd'): string[] => {
  const today = DateTime.local();
  const startOfWeek = today.startOf('week');
  const endOfWeek = today.endOf('week');
  return [startOfWeek.toFormat(format), endOfWeek.toFormat(format)];
};

export const formatTimeStamp = (timeStampStr: string): string => {
  const dt = DateTime.fromISO(timeStampStr, { setZone: true });

  if (dt.isValid) {
    return dt.toLocaleString({
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  throw Error('Invalid timestamp');
};

export const getTime = (timeStamp: string): string => {
  // server timezone could be UTC, but we need to display the hour/minute in the client's timezone
  const dt = DateTime.fromISO(timeStamp, { setZone: true });
  if (dt.isValid) {
    return dt.toFormat('HH:mm');
  }
  throw Error('Invalid timestamp');
};

const getAbbreviatedTimeZone = (timeZone: string): string => {
  const dummyDate = new Date();
  const timeZoneOptions = { timeZone, timeZoneName: 'short' };
  return new Intl.DateTimeFormat('en-US', timeZoneOptions as any)
    .formatToParts(dummyDate)
    .find((part) => part.type === 'timeZoneName')?.value || timeZone;
};

export const toLocalTimeZone = (timeStamp: string, format = false, showTimeZone = false): string => {
  const dt = DateTime.fromISO(timeStamp);
  if (!dt.isValid) {
    throw Error('Invalid timestamp');
  }

  const localDateTime = dt.toLocal();

  if (format) {
    const formattedDateTime = localDateTime.toLocaleString({
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    if (showTimeZone) {
      const localTimeZone = localDateTime.zoneName;
      const abbreviatedTimeZone = getAbbreviatedTimeZone(localTimeZone);
      return `${formattedDateTime} (${abbreviatedTimeZone})`;
    }

    return formattedDateTime;
  }

  if (showTimeZone) {
    const localTimeZone = localDateTime.zoneName;
    const abbreviatedTimeZone = getAbbreviatedTimeZone(localTimeZone);
    return `${localDateTime.toISO()} (${abbreviatedTimeZone})`;
  }

  return localDateTime.toISO();
};

export const getDateFromTimeStamp = (timeStamp: string): string => {
  const dt = DateTime.fromISO(timeStamp);
  if (dt.isValid) {
    return dt.toFormat('yyyy-MM-dd');
  }
  throw Error('Invalid timestamp');
};

// This maps a timestamp in whatever ISO format into a timestamp with zulu timezone
export const normalizeTimeStamp = (
  timeStamp: string,
  format: 'ISO' | 'HTTP' | 'RFC2822' | 'Millis' | undefined = 'ISO'
): string => {
  let dt;
  switch (format) {
    case 'ISO':
      dt = DateTime.fromISO(timeStamp);
      break;
    case 'HTTP':
      dt = DateTime.fromHTTP(timeStamp);
      break;
    case 'RFC2822':
      dt = DateTime.fromRFC2822(timeStamp);
      break;
    case 'Millis':
      dt = DateTime.fromMillis(parseInt(timeStamp));
      break;
  }
  if (dt.isValid) {
    return dt.toUTC().toISO();
  }
  throw Error('Invalid timestamp');
};

export const isDateGreaterThan = (dateA: string, dateB: string): boolean => {
  const dateADt = DateTime.fromISO(dateA);
  const dateBDt = DateTime.fromISO(dateB);

  return dateADt > dateBDt;
};

export const buildTimeStamp = (date: string, time: string, timezone: string | undefined = undefined): string => {
  if (timezone) {
    return DateTime.fromISO(`${date}T${time}`).setZone(timezone).toISO();
  }
  return toLocalTimeZone(`${date}T${time}`);
};
